import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template39: Página Tiendas originales en Tokio (Shops)
// Basado en Shops.jsx de guia-tokio; todo el contenido viene de page (backend).
// Valores CSS en píxeles para evitar que MUI multiplique por 8.
const Template39 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [failedShopImageIds, setFailedShopImageIds] = useState({});
	const [sticker1Error, setSticker1Error] = useState(false);
	const [sticker2Error, setSticker2Error] = useState(false);
	const [sticker3Error, setSticker3Error] = useState(false);

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const headerText = page.texts?.[0]?.content;
	const introText = page.texts?.[1]?.content;
	const footerText = page.texts?.[2]?.content;
	const shops =
		Array.isArray(page.shops)
			? page.shops
			: Array.isArray(page.data?.shops)
			? page.data.shops
			: Array.isArray(page.data?.data?.shops)
			? page.data.data.shops
			: [];

	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
			? page.data.order
			: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const sticker1 = getImageSrc("shop-1");
	const sticker2 = getImageSrc("shop-2");
	const sticker3 = getImageSrc("shop-3");

	const h4 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		fontWeight: 600,
		margin: "6px 0 4px",
		textAlign: "left",
	};

	const contentColor = textColor || "#444";

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: "0px",
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
			}}
		>
			{/* CABECERA */}
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0px",
						fontSize: "14px",
						fontWeight: 800,
						flexShrink: 0,
						position: "sticky",
						top: "0px",
						zIndex: 99999,
					}}
				>
					{headerText}
				</Box>
			)}

			{/* INTRO */}
			{introText && (
				<Box
					data-block-id="text-1"
					sx={{
						padding: "16px 16px",
						fontSize: "13px",
						color: contentColor,
					}}
				>
					<Box component="p" sx={p}>
						{introText}
					</Box>
				</Box>
			)}

			{/* TIENDAS - grid de tarjetas */}
			<Box
				data-block-id="text-2"
				sx={{
					padding: "0px 12px 12px",
					display: "flex",
					flexWrap: "wrap",
					gap: "12px",
					position: "relative",
					justifyContent: "center",
				}}
			>
				{shops.map((shop) => (
					<Box
						key={shop.name}
						sx={{
							backgroundColor: "#fff",
							borderRadius: "12px",
							width: isSmallScreen ? "30%" : "20%",
							padding: "8px",
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-start",
						}}
					>
						{shop.imageId && (
							getImageSrc(shop.imageId) && !failedShopImageIds[shop.imageId] ? (
								<Box
									component="img"
									src={getImageSrc(shop.imageId)}
									alt={shop.name}
									onError={() => setFailedShopImageIds((prev) => ({ ...prev, [shop.imageId]: true }))}
									sx={{
										width: "100%",
										height: "70px",
										objectFit: "cover",
										borderRadius: "8px",
									}}
								/>
							) : failedShopImageIds[shop.imageId] ? (
								<ImagenNoDisponibleKawaii width="100%" height={70} fontFamily={fontFamily || "'Poppins', sans-serif"} compact />
							) : null
						)}
						<Box component="h4" sx={{ ...h4, textAlign: "left" }}>
							{shop.name}
						</Box>
						<Box component="p" sx={{ ...p, textAlign: "left" }}>
							{shop.address}
						</Box>
					</Box>
				))}

				{sticker1 && !sticker1Error && (
					<Box
						component="img"
						src={sticker1}
						alt=""
						onError={() => setSticker1Error(true)}
						sx={{
							width: isSmallScreen ? "90px" : "120px",
							position: "absolute",
							top: isSmallScreen ? "-40px" : "-30px",
							right: "-30px",
						}}
					/>
				)}
				{sticker2 && !sticker2Error && (
					<Box
						component="img"
						src={sticker2}
						alt=""
						onError={() => setSticker2Error(true)}
						sx={{
							width: isSmallScreen ? "80px" : "110px",
							position: "absolute",
							bottom: "-90px",
							left: "-20px",
						}}
					/>
				)}
				{sticker3 && !sticker3Error && (
					<Box
						component="img"
						src={sticker3}
						alt=""
						onError={() => setSticker3Error(true)}
						sx={{
							width: isSmallScreen ? "80px" : "100px",
							position: "absolute",
							bottom: "-90px",
							right: "-21px",
						}}
					/>
				)}
			</Box>

			{/* TEXTO FINAL */}
			{footerText && (
				<Box
					sx={{
						padding: "0px 16px 20px",
						textAlign: "center",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<Box
						component="p"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: "13px",
							color: contentColor,
							margin: "0px",
							maxWidth: isSmallScreen ? "200px" : "380px",
						}}
					>
						{footerText}
					</Box>
				</Box>
			)}
		</Box>
	);
};

Template39.thumbnail = "/images/template-1.png";

export default Template39;
